<template>
  <div
    class="search-course-resource-detail form-group"
    :class="{
      'show-notes': dataShowNotes,
    }"
  >
    <cv-toggle
      v-if="canToggleNotes"
      small
      class="notes-toggle"
      :label="$t('search_resource_detail_notes_toggle')"
      value="notes-toggle"
      :checked="dataShowNotes"
      @change="handleNotesToggle"
    >
      <template #text-left>{{ emptyText }}</template>
      <template #text-right>{{ emptyText }}</template>
    </cv-toggle>

    <div v-if="isPasscodeEnabled" class="passcode-info">
      <svg-icon name="Passcode16" kind="indicative" />
      <span>{{ $t("search_resource_detail_passcode_enabled") }}</span>
    </div>

    <cv-form>
      <div class="section-activity-list">
        <cv-inline-notification
          v-if="showEmptyCourseBanner"
          :title="$t('search_resource_detail_empty_course_info_banner')"
          low-contrast
          class="banner"
          kind="info"
          hide-close-button
        />
        <data-table-list v-else class="section-activity-list-datatable" in-subpage>
          <template v-if="showRowSelected" #selection-bar>
            <selection-bar :count="rowsSelected" @clear="handleClearSelection" />
          </template>

          <!-- section list -->
          <template v-if="showSectionCover || showDisabledSection">
            <div
              v-for="section in sectionList"
              :key="section.id"
              class="section-grouper grouper"
              :class="{ disabled: isDisabled }"
              :style="cssVars"
            >
              <div class="section-component">
                <match-indicator v-if="checkSectionIdMatched(section.id)" />
                <div
                  class="wrapper"
                  :class="{
                    'has-input-box': hasInputbox,
                  }"
                >
                  <div v-if="showSelectButtonForSection" class="all-checkbox" @click.stop>
                    <input-checkbox
                      :id="`section-list-${section.id.toString()}`"
                      :value="section.id.toString()"
                      :selected-values="showSectionCover ? selectAllActivities : []"
                      :disabled="
                        showSectionCover ? isCheckBoxDisabledForSection : showDisabledSection
                      "
                      @change="handleSelectAll(section.id, $event)"
                    />
                  </div>

                  <div v-if="showRadioButtonForSection" class="all-checkbox" @click.stop>
                    <input-radio
                      :id="`section-list-${section.id.toString()}`"
                      kind="white"
                      :value="section.id.toString()"
                      :selected-value="selectActivity"
                      :disabled="isRadioButtonDisabledForSection"
                      @change="handleSelectOneSection(section.id)"
                    />
                  </div>
                  <span class="icon">
                    <svg-icon v-if="showSectionCover" name="Section32" kind="indicative" />
                    <svg-icon
                      v-else-if="showDisabledSection"
                      name="SectionHidden32"
                      kind="indicative"
                    />
                    <span v-if="!hideSectionActivityNumber" class="page-label">
                      {{ sectionLabel(section.pageNo) }}
                    </span>
                  </span>
                  <span class="page-title">{{ section.title }}</span>
                  <svg-icon
                    v-if="showOpenSectionInNewTabIcon"
                    class="view-ext"
                    name="ExternalLink24"
                    kind="action"
                    @click="handleOpenSectionInNewTab(section.id)"
                  />

                  <div class="additional-information">
                    <label>
                      <svg-icon name="ActivityNote24" kind="indicative" />
                      <span>{{ $t("search_resource_detail_section_notes_label") }}</span>
                    </label>

                    <empty-placeholder v-if="isNotesEmpty(section.notes)">
                      <template #icon>
                        <AnnotationCardsEmpty />
                      </template>
                      {{ $t("search_resource_detail_section_notes_placeholder") }}
                    </empty-placeholder>
                    <rich-text v-else class="notes" :content="section.notes" />
                  </div>
                </div>
              </div>

              <!-- toggle section -->
              <div class="section-info">
                <div class="actions-buttons">
                  <cv-link @click="handleToggleExpand(section.id)">
                    {{ sectionToggleLabel(section) }}
                  </cv-link>
                </div>
              </div>

              <!-- activity list -->
              <div v-if="showActivityList(section.id)" class="section-activities">
                <!--FIXME VUE3: CURRENTLY NOT SUPPORTED
                  mobile-breakpoint="0"
                -->
                <v-data-table
                  :headers="getDatatableHeaders"
                  :items="getActivityList(section.id)"
                  :items-per-page="20"
                  fixed-header
                  mobile-breakpoint="0"
                  item-value="id"
                >
                  <!--                  todo: cj to check if needed the slot since header does not exist -> column -->
                  <!--                  https://github.com/vuetifyjs/vuetify/pull/18619-->
                  <template #[`header.title`]="{ column }">
                    <div v-if="params.hasSelectAction" class="all-checkbox" @click.stop>
                      <input-checkbox
                        :id="`activity-list-all-${section.id.toString()}`"
                        :value="section.id.toString()"
                        :selected-values="selectAllActivities"
                        :mixed="showMixedCheckbox(section.id)"
                        @change="handleSelectAll(section.id, $event)"
                      />
                    </div>
                    <span>{{ column.title }}</span>
                  </template>
                  <template #[`item.title`]="{ item }">
                    <match-indicator v-if="checkActivityIdMatched(item.id)" />
                    <data-table-cell
                      class="title"
                      :class="{
                        'has-input-box': hasInputbox,
                        'simple-view': !params.canViewInNewTab,
                      }"
                    >
                      <div class="activity-header-wrapper">
                        <div
                          v-if="showSelectButtonForActivity"
                          class="activity-checkbox"
                          @click.stop
                        >
                          <input-checkbox
                            :id="`activity-list-${item.id.toString()}`"
                            :value="item.id.toString()"
                            :selected-values="selectedActivities"
                            :disabled="isCheckBoxDisabledForActivity"
                            @change="handleSelect(section.id, $event)"
                          />
                        </div>
                        <div
                          v-if="showRadioButtonForActivity"
                          class="activity-checkbox"
                          @click.stop
                        >
                          <input-radio
                            :id="`activity-list-${item.id.toString()}`"
                            kind="grey"
                            :value="item.id.toString()"
                            :selected-value="selectActivity"
                            :disabled="isRadioButtonDisabledForDifferentActivityType(item)"
                            @change="handleSelectOneActivity($event)"
                          />
                        </div>
                        <span class="icon">
                          <svg-icon :name="getActivityIcon(item)" kind="indicative" />
                          <span v-if="!hideSectionActivityNumber" class="page-label">
                            {{ item.pageNo }}
                          </span>
                        </span>
                        <div class="activity-title-wrapper">
                          <span class="page-title">{{ item.title }}</span>
                          <label-component
                            v-if="!isCustomTag(item)"
                            kind="alp-stage"
                            :class="item.alpStage"
                            :text="item.alpStageDisplayName"
                          />
                        </div>
                        <svg-icon
                          v-if="params.canViewInNewTab"
                          class="view-ext"
                          name="ExternalLink24"
                          kind="action"
                          @click="handleOpenActivityInNewTab(section.id, item.id)"
                        />
                      </div>

                      <div class="additional-information">
                        <label>
                          <svg-icon name="ActivityNote24" kind="indicative" />
                          <span>{{ $t("search_resource_detail_activity_notes_label") }}</span>
                        </label>
                        <empty-placeholder v-if="isNotesEmpty(item.notes)">
                          <template #icon>
                            <AnnotationCardsEmpty />
                          </template>
                          {{ $t("search_resource_detail_activity_notes_placeholder") }}
                        </empty-placeholder>
                        <rich-text v-else class="notes" :content="item.notes" />
                      </div>
                    </data-table-cell>
                  </template>
                  <template #bottom></template>
                </v-data-table>
              </div>
            </div>
          </template>

          <div v-else class="activity-grouper grouper" :class="{ disabled: isDisabled }">
            <!--FIXME VUE3: CURRENTLY NOT SUPPORTED
                mobile-breakpoint="0"
            -->
            <v-data-table
              v-model="selectedActivities"
              :show-select="showSelectButtonForActivity"
              :headers="getDatatableHeaders"
              :items="getAllActivityList()"
              :items-per-page="20"
              fixed-header
              item-value="id"
              mobile-breakpoint="0"
              :class="{ '_items-selectable': showSelectButtonForActivity }"
            >
              <template #[`header.data-table-select`]>
                <input-checkbox
                  :id="`activity-grouper-all-${sectionList[0].id.toString()}`"
                  :value="sectionList[0].id.toString()"
                  :selected-values="selectAllActivities"
                  :mixed="showMixedCheckbox(sectionList[0].id)"
                  @change="handleSelectAll(sectionList[0].id, $event)"
                />
              </template>
              <template #[`item.data-table-select`]="{ item }">
                <data-table-cell class="checkbox">
                  <input-checkbox
                    :id="`activity-grouper-${item.id.toString()}`"
                    kind="white"
                    :value="item.id.toString()"
                    :selected-values="selectedActivities"
                    @change="handleSelect(sectionList[0].id, $event)"
                  />
                </data-table-cell>
              </template>
              <template #[`item.title`]="{ item }">
                <match-indicator v-if="checkActivityIdMatched(item.id)" />
                <data-table-cell
                  class="title"
                  :class="{
                    'has-input-box': hasInputbox,
                    'simple-view': !params.canViewInNewTab,
                  }"
                >
                  <div class="activity-header-wrapper">
                    <span class="icon">
                      <svg-icon :name="getActivityIcon(item)" kind="indicative" />
                      <span v-if="!hideSectionActivityNumber" class="page-label">
                        {{ item.pageNo }}
                      </span>
                    </span>
                    <div class="activity-title-wrapper">
                      <span class="page-title">{{ item.title }}</span>
                      <label-component
                        v-if="!isCustomTag(item)"
                        kind="alp-stage"
                        :class="item.alpStage"
                        :text="item.alpStageDisplayName"
                      />
                    </div>
                    <svg-icon
                      v-if="params.canViewInNewTab"
                      class="view-ext"
                      name="ExternalLink24"
                      kind="action"
                      @click="handleOpenActivityInNewTab(item.parentId, item.id)"
                    />
                  </div>

                  <div class="additional-information">
                    <label>
                      <svg-icon name="ActivityNote24" kind="indicative" />
                      <span>{{ $t("search_resource_detail_activity_notes_label") }}</span>
                    </label>
                    <empty-placeholder v-if="isNotesEmpty(item.notes)">
                      <template #icon>
                        <AnnotationCardsEmpty />
                      </template>
                      {{ $t("search_resource_detail_activity_notes_placeholder") }}
                    </empty-placeholder>
                    <rich-text v-else class="notes" :content="item.notes" />
                  </div>
                </data-table-cell>
              </template>
              <template #bottom></template>
            </v-data-table>
          </div>
        </data-table-list>
      </div>
    </cv-form>

    <!-- Game Details -->
    <cv-form v-if="showGamification" :id="gameBookSectionAnchorId" class="game-details">
      <h5 class="form-header">
        <span>{{ $t("search_resource_detail_game_detail_label") }}</span>
      </h5>
      <dl class="field-set">
        <dd class="field-value">
          <SearchResourceDetailsGameBookSection
            :params="params"
            :is-selectable="isGameBookSelectable"
            :is-selected="isGameBookSelected"
            @selected="handleGameBookSelected"
          />
        </dd>
      </dl>
    </cv-form>

    <!-- Location -->
    <cv-form v-if="showLocation" class="location-details">
      <dl class="field-set type-text location">
        <dt class="field-label">{{ $t("search_resource_detail_location_label") }}</dt>
        <dd class="field-value">
          <span class="message">
            {{ params.location }}
          </span>
        </dd>
      </dl>
    </cv-form>

    <!-- Course Details -->
    <cv-form class="course-details">
      <h5 class="form-header other-information">
        <span>{{ $t("search_resource_detail_other_information_label") }}</span>
      </h5>
      <!-- subject and level -->
      <match-indicator v-if="matchSubjectAndLevel" />
      <dl v-if="showSubjectAndLevel" class="field-set type-text subject">
        <dt class="field-label">{{ $t("search_resource_detail_subject_and_level_label") }}</dt>
        <dd class="field-value">
          <data-table-list class="subject-and-level-datatable" in-subpage>
            <!--FIXME VUE3: CURRENTLY NOT SUPPORTED
              mobile-breakpoint="0"
            -->
            <v-data-table
              :items="sortSubjectLevelTaggings"
              :headers="subjectAndLevelDataTableHeaders"
              :items-per-page-options="subjectAndLevelDataTableFooterProps.itemsPerPageOptions"
              fixed-header
              mobile-breakpoint="0"
            >
              <!-- Subject -->
              <template #[`item.subjectName`]="{ item }">
                <data-table-cell class="subject">
                  <span v-if="item.subjectName" :title="item.subjectName" class="main">
                    {{ `${item.subjectName} - ${item.subjectKey}` }}
                  </span>
                  <span v-else class="main" />
                </data-table-cell>
              </template>

              <!-- Level -->
              <template #[`item.levelAndStream`]="{ item }">
                <data-table-cell class="level">
                  <span v-if="item.levelAndStream" :title="item.levelAndStream" class="main">
                    {{ item.levelAndStream }}
                  </span>
                  <span v-else class="main" />
                </data-table-cell>
              </template>
            </v-data-table>
          </data-table-list>
        </dd>
      </dl>
      <!-- contributors -->
      <match-indicator v-if="matchContributors" />
      <dl class="field-set type-text contributors">
        <dt class="field-label">{{ $t("search_resource_detail_credited_label") }}</dt>
        <dd class="field-value">
          <div class="output-text">
            <label-component
              kind="creditor"
              :class="{ 'creditor-moe': !canOpenProfile }"
              :text="contributors"
              @click="handleProfileClick(contributorsId, isMOEResource)"
            />
          </div>
          <div v-if="showTruncate(1)" class="truncate" />
          <time class="date">{{ createdDate }}</time>
        </dd>
      </dl>
      <!-- modified by -->
      <dl class="field-set type-text modified-by">
        <dt class="field-label">{{ $t("search_resource_detail_edited_label") }}</dt>
        <dd class="field-value">
          <label-component
            kind="creditor"
            :text="modifiedBy"
            @click="handleProfileClick(modifiedById, false)"
          />
          <time class="date">{{ modifiedDate }}</time>
        </dd>
      </dl>
      <!-- credited to -->
      <dl class="field-set type-text creditedTo">
        <dt class="field-label">{{ $t("search_resource_detail_credited_to_label") }}</dt>
        <dd class="field-value">
          <div class="creditedToContent" :class="creditedToClass">
            <template v-if="showCreditedTo">
              <span v-for="(tag, index) in creditedTo" :key="index">
                <label-component
                  :text="tag.username"
                  kind="creditor"
                  @click="handleProfileClick(tag.userId, false)"
                />
              </span>
            </template>
            <label-component v-else text="-" class="blank" kind="creditor" />
          </div>
          <cv-link
            v-if="truncateCreditedTo && showCreditedTo"
            class="btn-toggle-truncation"
            @click="handleToggleTruncation"
          >
            <!-- Read More / Read Less -->
            <span class="truncation-toggle">{{ truncateActionLabel }}</span>
          </cv-link>
        </dd>
      </dl>
      <!-- View count -->
      <dl v-if="showViewCount" class="field-set type-text view-count">
        <dt class="field-label">{{ $t("search_resource_detail_view_count_label") }}</dt>
        <dd class="field-value">
          <svg-icon name="View24" kind="action" />
          <span>{{ viewCount }}</span>
        </dd>
      </dl>
      <match-indicator v-if="matchKeyword" />
      <!-- Keywords -->
      <dl class="field-set type-text keywords">
        <dt class="field-label">{{ $t("search_resource_detail_keywords_label") }}</dt>
        <dd class="field-value">
          <keywords :tags="keywords" />
        </dd>
      </dl>
    </cv-form>
  </div>
</template>

<script lang="ts">
import { mapGetters } from "vuex";
import SettingsSummaryMixin from "@modules/editor/mixin/settings.summary.mixin";
import StringUtils from "@utils/string.utils";
import DateTimeFormatter from "@utils/datetime.formatter";
import { LOCATION_TYPE, RESOURCE_DOMAIN, NAMESPACE } from "@modules/search/service/search.constant";
import {
  LESSON_ACTIVITY_TYPE,
  RESOURCE_DOMAIN_PATH,
} from "@plugins/common/constant/resource.constant.ts";
import { isDefined, isEmpty } from "@utils/var.check";
import AssignmentUtils from "@modules/assignment/service/assignment.utils";
import ActivityIconMixin from "@plugins/common/mixin/activity.icon.mixin";
import SearchResourceDetailsGameBookSection from "@modules/search/components/SearchResourceDetailsGameBookSection";
import sortUtils from "@utils/sort.utils";
import AnnotationCardsEmpty from "@images/placeholder/empty-states/AnnotationCardsEmpty.svg?component";
import Keywords from "@plugins/keywords/components/Keywords";
import { COMPONENT_AUTHORING_COPILOT_TYPE } from "@modules/editor/service/editor.constant";
import AuthoringCopilotTemplateSelectionMixin from "@modules/editor/mixin/authoring.copilot.template.selection.mixin";
import resourceApi from "@/apis/resource.api";
import SearchProfile from "@modules/search/mixin/search.profile.mixin.ts";
import DOMUtils from "@utils/dom.utils";

export default {
  name: "SearchCourseResourceDetail",
  components: {
    SearchResourceDetailsGameBookSection,
    AnnotationCardsEmpty,
    Keywords,
  },
  mixins: [
    SettingsSummaryMixin,
    ActivityIconMixin,
    AuthoringCopilotTemplateSelectionMixin,
    SearchProfile,
  ],
  props: {
    params: { type: Object, default: null },
    context: { type: String, required: true },
    selectionMode: { type: String, default: "" },
  },
  emits: [
    "activitiesSelected",
    "sectionsSelected",
    "gameBookSelected",
    "update:selectionMode",
    "toggle-notes",
    "anchor",
  ],
  data() {
    return {
      sectionList: [],
      expandedList: {},
      selectedActivities: [],
      selectAllActivities: [],
      selectedGameBookId: null,
      dataShowNotes: false,
      subjectAndLevelDataTableHeaders: [],
      subjectAndLevelDataTableFooterProps: {},
      selectActivity: "", //to show selection only
      creditedTo: [],
      truncateCreditedTo: true,
      isCreditedToExpanded: false,
      truncationType: "primary",
    };
  },
  computed: {
    ...mapGetters(NAMESPACE.AUTH, ["isStudent", "isTeacher"]),
    emptyText() {
      return "";
    },
    showGamification() {
      return this.params.lesson ? this.params.lesson.gameEnabled : false;
    },
    showLocation() {
      return this.params.type !== "ASSIGNMENT_LESSON";
    },
    showSubjectAndLevel() {
      return !isEmpty(this.subjectAndLevelList);
    },
    subjectAndLevelList() {
      return this.params.lesson.subjectAndLevelList;
    },
    sortSubjectLevelTaggings() {
      // Check if subjectLevelTaggings is empty
      if (isEmpty(this.subjectAndLevelList)) return [];

      // Define the sorting configuration
      const sortConfig = [
        { field: "subjectName", asc: true },
        { field: "levelAndStream", asc: true },
      ];

      // Sort the subjectLevelTaggings using the sortObjects method
      const sortedSubjectLevelTaggings = sortUtils.sortObjects(
        this.subjectAndLevelList,
        sortConfig,
      );

      return sortedSubjectLevelTaggings;
    },
    showEmptyCourseBanner() {
      return this.sectionList.length === 0;
    },
    showActivityList() {
      return (sectionId) => {
        if (this.getActivityList(sectionId).length === 0) {
          return this.isExpanded(sectionId) && this.params.hasSelectAction;
        } else return this.isExpanded(sectionId);
      };
    },
    getDatatableHeaders() {
      return [
        {
          title: this.$i18n.t("search_lesson_preview_datatable_header_activity"),
          key: "title",
          sortable: false,
        },
      ];
    },
    lessonActivityList() {
      return this.params.lesson ? this.params.lesson.lessonActivityList : [];
    },
    isCustomTag() {
      return (item) => {
        return item.alpStage === "CUSTOM";
      };
    },
    sectionLabel() {
      return (pageNo) => {
        return pageNo ? StringUtils.getAlphabeticalLetter(pageNo - 1) : "";
      };
    },
    sectionToggleLabel() {
      return (section) => {
        const length = this.getActivityList(section.id).length;
        return this.isExpanded(section.id)
          ? this.$i18n.t("search_resource_detail_toggle_hide_label", {
              activityLength: length,
            })
          : this.$i18n.t("search_resource_detail_toggle_show_label", {
              activityLength: length,
            });
      };
    },
    isExpanded() {
      return (sectionId) => {
        return this.expandedList[sectionId].expanded;
      };
    },
    contributors() {
      if (
        this.params.lesson.resourceDomain === LOCATION_TYPE[2].value ||
        this.params.lesson.publisher === LOCATION_TYPE[2].value
      ) {
        return (
          this.$i18n.t("search_resource_detail_publisher_label") +
          ` ${this.params.lesson.ownerGroup?.name ?? ""}`
        );
      } else {
        const originator = this.params.lesson.originator;
        const originatorName = this.params.lesson.originatorName;
        if (isDefined(originator)) {
          return originator.name;
        } else if (originatorName) {
          return originatorName;
        }
        return "-";
      }
    },
    contributorsId() {
      if (
        this.params.lesson.resourceDomain === LOCATION_TYPE[2].value ||
        this.params.lesson.publisher === LOCATION_TYPE[2].value
      ) {
        return 0;
      } else {
        const originator = this.params.lesson.originator;
        const originatorId = this.params.lesson.originatorId;
        if (isDefined(originator)) {
          return originator.id;
        } else if (originatorId) {
          return originatorId;
        }
        return 0;
      }
    },
    modifiedBy() {
      return this.params.lesson.modifiedByName;
    },
    modifiedById() {
      return this.params.lesson.modifiedById;
    },
    modifiedDate() {
      return DateTimeFormatter.formatDateTimeToDateTimeAmPm(this.params.lesson.modifiedDate);
    },
    createdDate() {
      return DateTimeFormatter.formatDateTimeToDateTimeAmPm(this.params.lesson.createdDate);
    },
    showMixedCheckbox() {
      //show if >0 but not all activities in section are checked
      return (sectionId) => {
        const activityList = this.getActivityList(sectionId);
        const filteredActivityList = activityList.filter((activity) =>
          this.selectedActivities.includes(activity.id.toString()),
        );
        return (
          filteredActivityList.length > 0 && filteredActivityList.length !== activityList.length
        );
      };
    },
    rowsSelected() {
      if (this.showSectionCover)
        return this.selectedActivities.length + this.selectAllActivities.length;
      return this.selectedActivities.length;
    },
    showViewCount() {
      return (
        this.params.lesson.resourceDomain !== RESOURCE_DOMAIN.PERSONAL &&
        this.params.lesson.resourceDomain !== RESOURCE_DOMAIN.ALP_TEMPLATE &&
        this.params.lesson.resourceDomain !== RESOURCE_DOMAIN.ASSIGNMENT
      );
    },
    viewCount() {
      return this.params.lesson && !isEmpty(this.params.lesson.viewCount)
        ? this.params.lesson.viewCount
        : 0;
    },
    keywords() {
      return this.params.lesson.keywords ? this.params.lesson.keywords : "-";
    },
    cssVars() {
      return {
        "--color": "#5885DE",
      };
    },
    cssClasses() {
      return {
        "is-section-hidden": this.showDisabledSection,
        disabled: this.isDisabled,
      };
    },
    hideSectionActivityNumber() {
      return this.params.lesson.hideSectionActivityNumber;
    },
    showSectionCover() {
      return this.params.lesson.showSectionCover;
    },
    isPasscodeEnabled() {
      return this.params.lesson.passcodeEnabled;
    },
    isUpcomingAssignment() {
      return (
        this.params.lesson.resourceDomain === RESOURCE_DOMAIN.ASSIGNMENT &&
        this.params.lesson.upcomingAssignment
      );
    },
    isDisabled() {
      return (this.isPasscodeEnabled || this.isUpcomingAssignment) && this.isStudent;
    },
    canToggleNotes() {
      return this.isTeacher;
    },
    isSectionSelectionMode() {
      return this.selectionMode === LESSON_ACTIVITY_TYPE.SECTION;
    },
    isActivitySelectionMode() {
      return this.selectionMode === LESSON_ACTIVITY_TYPE.ACTIVITY;
    },
    isFromGamification() {
      return this.params.isFromGamification;
    },
    isGameBookSelectable() {
      return this.isFromGamification;
    },
    gameBookSectionAnchorId() {
      if (this.params?.lesson?.gameBookModel?.id) {
        return `game-book-section-${this.params?.lesson?.gameBookModel?.id}`;
      }
      return null;
    },
    matchKeyword() {
      return isEmpty(this.params.highlights) ? null : this.params.highlights.keyword;
    },
    showDisabledSection() {
      return isEmpty(this.params.highlights) ? null : this.params.highlights;
    },
    showOpenSectionInNewTabIcon() {
      return this.showSectionCover && this.params.canViewInNewTab;
    },
    showRowSelected() {
      return !this.isFromAuthoringCopilot;
    },
    showSelectButtonForSection() {
      //hide if coming from authoring copilot or gamification
      if (this.isFromAuthoringCopilot || this.isFromGamification) {
        return false;
      }
      return this.params.hasSelectAction;
    },
    showRadioButtonForSection() {
      //hide if coming from gamification
      if (this.isFromGamification) {
        return false;
      }
      return this.isFromLessonActivityAuthoringCopilot;
    },
    showSelectButtonForActivity() {
      if (this.isFromLessonActivityAuthoringCopilot) {
        return true;
      } else if (
        this.isFromLessonModuleAuthoringCopilot ||
        this.isFromLessonComponentAuthoringCopilot
      ) {
        return false;
      }
      if (this.isFromGamification) {
        return false;
      }
      return this.params.hasSelectAction;
    },
    showRadioButtonForActivity() {
      //hide if coming from gamification
      if (this.isFromGamification) {
        return false;
      }
      return this.isFromLessonComponentAuthoringCopilot;
    },
    isCheckBoxDisabledForActivity() {
      if (this.isFromAuthoringCopilotViewDetails || this.isFromLessonActivityAuthoringCopilot)
        return true;
      return this.isSectionSelectionMode;
    },
    isCheckBoxDisabledForSection() {
      if (this.isFromAuthoringCopilotViewDetails) return true;
      return this.isActivitySelectionMode;
    },
    isRadioButtonDisabledForSection() {
      return this.isFromAuthoringCopilotViewDetails;
    },
    hasInputbox() {
      return this.showSelectButtonForSection || this.showRadioButtonForSection;
    },
    matchSubjectAndLevel() {
      return isEmpty(this.params.highlights)
        ? null
        : this.params.highlights.subjectMap || this.params.highlights.levelAndStreamMap;
    },
    matchContributors() {
      return isEmpty(this.params.highlights) ? null : this.params.highlights.createdBy;
    },
    showCreditedTo() {
      return this.creditedTo && this.creditedTo.length > 0;
    },
    isMOEResource() {
      return this.params.lesson.resourceDomain === RESOURCE_DOMAIN.MOE;
    },
    canOpenProfile() {
      return !this.isMOEResource || this.contributorsId !== 0;
    },
    creditedToClass() {
      return [
        {
          collapsed: this.showCreditedTo && this.truncateCreditedTo && !this.isCreditedToExpanded,
        },
        `${this.truncationType}-truncation`,
      ];
    },
    truncateActionLabel() {
      return this.isCreditedToExpanded ? this.$i18n.t("read_less") : this.$i18n.t("read_more");
    },
    isGameBookSelected() {
      return notNull(this.selectedGameBookId);
    },
  },
  watch: {
    selectedActivities: {
      handler() {
        if (!this.isFromAuthoringCopilotViewDetails) {
          this.$emit("activitiesSelected", this.selectedActivities);
        }
      },
      deep: true,
    },
    selectAllActivities: {
      handler() {
        if (
          (this.isFromAuthoringCopilot || this.showSectionCover) &&
          !this.isFromAuthoringCopilotViewDetails
        ) {
          this.$emit("sectionsSelected", this.selectAllActivities);
        }
      },
      deep: true,
    },
  },
  mounted() {
    if (this.isFromGamification) {
      this.emitScrollEvent(this.gameBookSectionAnchorId);
    }
  },
  created() {
    this.getSectionList();
    this.initialiseExpandedList();
    if (this.isFromLessonComponentAuthoringCopilot) {
      this.expandAllSections();
      if (this.isFromAuthoringCopilotViewDetails) {
        this.handleSelectOneActivity(this.params.templateSelected.referenceLessonActivityId);
      }
    } else if (
      this.isFromLessonModuleAuthoringCopilot ||
      this.isFromLessonActivityAuthoringCopilot
    ) {
      this.collapseAllSections();
    }
    if (this.isFromLessonActivityAuthoringCopilot && this.isFromAuthoringCopilotViewDetails) {
      this.handleSelectOneSection(this.params.templateSelected.referenceLessonActivityId);
    }
    this.initSubjectAndLevelDataTableOption();
    if (!this.isMOEResource) {
      this.fetchContributors().then(() => {
        this.determineCreditedToTruncation();
      });
    }
    this.initSelectedGameBook();
  },
  methods: {
    isRadioButtonDisabledForDifferentActivityType(activity) {
      if (this.isFromAuthoringCopilotViewDetails) return true;
      let activityType = null;
      switch (this.params.activityType) {
        case COMPONENT_AUTHORING_COPILOT_TYPE.QUIZ:
          activityType = LESSON_ACTIVITY_TYPE.ACTIVITY_QUIZ;
          break;
        case COMPONENT_AUTHORING_COPILOT_TYPE.ACTIVITY:
          activityType = LESSON_ACTIVITY_TYPE.ACTIVITY;
          break;
        default:
          return true;
      }

      return activity.type !== activityType;
    },
    getActivityIcon(page) {
      return this.getActivityIconNameByTypeAndOptional(page.type, page.optional);
    },
    getSectionList() {
      this.lessonActivityList.forEach((activity) => {
        if (activity.type === LESSON_ACTIVITY_TYPE.SECTION) {
          this.sectionList.push(activity);
        }
      });
      return this.sectionList.sort((sectionA, sectionB) => {
        return sectionA.pageNo > sectionB.pageNo ? 1 : -1;
      });
    },
    getActivityList(sectionId) {
      const activityList = [];
      this.lessonActivityList.forEach((activity) => {
        if (
          AssignmentUtils.isActivityOrQuizActivity(activity.type) &&
          activity.parentId === sectionId
        ) {
          activityList.push(activity);
        }
      });
      const filteredList = activityList.map((activity) => {
        return {
          id: activity.id,
          title: activity.title,
          pageNo: activity.pageNo,
          type: activity.type,
          optional: activity.optional,
          alpStage: activity.alpStage.name,
          notes: activity.notes,
          parentId: activity.parentId,
          alpStageDisplayName: activity.alpStage.displayName,
        };
      });
      const sortConfig = [{ field: "pageNo", asc: true }];
      return sortUtils.sortObjects(filteredList, sortConfig);
    },
    getAllActivityList() {
      const activityList = this.lessonActivityList.filter(
        (activity) => activity.type !== LESSON_ACTIVITY_TYPE.SECTION,
      );
      const filteredList = activityList.map((activity) => {
        return {
          id: activity.id,
          title: activity.title,
          pageNo: activity.pageNo,
          type: activity.type,
          optional: activity.optional,
          alpStage: activity.alpStage.name,
          notes: activity.notes,
          parentId: activity.parentId,
          alpStageDisplayName: activity.alpStage.displayName,
        };
      });
      const sortConfig = [{ field: "pageNo", asc: true }];
      return sortUtils.sortObjects(filteredList, sortConfig);
    },

    handleToggleExpand(sectionId) {
      if (this.isDisabled) return;

      const section = this.expandedList[sectionId];
      this.expandedList[sectionId] = {
        expanded: !section.expanded,
      };
    },
    initialiseExpandedList() {
      // set default to be collapsed, expand if match indicator
      this.sectionList.forEach((section) => {
        this.expandedList[section.id] = {
          expanded: false,
        };

        const activityList = this.getActivityList(section.id);
        for (const activity of activityList) {
          if (this.checkActivityIdMatched(activity.id)) {
            this.expandedList[section.id].expanded = true;
            break; // Exit the loop for this section if a match is found
          }
        }
      });
    },
    handleOpenActivityInNewTab(sectionId, pageId) {
      let routeName;
      let routeParams;
      let routeQuery;

      if (this.params.type === "ASSIGNMENT_LESSON") {
        // TODO: SLS-11043 (DONE), attempt
        const moduleUuid = this.params.lesson.uuid;
        routeName = this.isStudent ? "AssignmentAttemptActivityPage" : "AssignmentViewActivityPage";
        routeParams = {
          assignmentUuid: this.params.assignmentUuid,
          moduleUuid,
          sectionId,
          pageId: pageId,
        };
      } else {
        const isStudent = this.isStudent;
        let resourceUuid = this.params.lesson.uuid;
        let resourceDomain = "";
        switch (this.params.lesson.resourceDomain) {
          case "PERSONAL":
            routeName = isStudent ? "PersonalLessonAttemptActivityPage" : "EditorActivityPage";
            resourceDomain = RESOURCE_DOMAIN_PATH.PERSONAL;
            break;
          case "MOE":
            routeName = isStudent ? "SelfStudyAttemptActivityPage" : "EditorActivityPage";
            resourceDomain = RESOURCE_DOMAIN_PATH.MOE_LIBRARY;
            resourceUuid = this.params.lesson.moeLibraryUuid;
            routeQuery = this.params.lesson.uuid ? { version: this.params.lesson.uuid } : undefined;
            break;
          case "COMMUNITY":
          case "Community":
            routeName = "EditorActivityPage";
            resourceDomain = RESOURCE_DOMAIN_PATH.COMMUNITY_GALLERY;
            resourceUuid = this.params.communityResourceUuid;
            break;
          case "ALP_TEMPLATE":
            routeName = "EditorActivityPage";
            resourceDomain = RESOURCE_DOMAIN_PATH.ALP_TEMPLATE;
            resourceUuid = this.params.templateUuid;
            break;
          case "ASSIGNMENT":
            routeName = isStudent
              ? "StudentGroupResourceAttemptActivityPage"
              : "EditorActivityPage";
            resourceDomain = RESOURCE_DOMAIN_PATH.CLASS_GROUP_RESOURCE;
            break;
        }
        // TODO: SLS-11043 (DONE), editor, add sectionId
        // TODO: SLS-11043 (DONE), attempt, add sectionId
        routeParams = {
          resourceDomain: resourceDomain,
          lessonUuid: resourceUuid,
          mode: "view",
          sectionId,
          pageId: pageId,
        };

        if (this.params.admin && this.params.admin === "admin") {
          routeParams.admin = "admin";
        }
      }

      const routeData = this.$router.resolve({
        name: routeName,
        params: routeParams,
        query: routeQuery,
      });
      window.open(routeData.href, "_blank");
    },
    handleOpenSectionInNewTab(sectionId) {
      if (this.isDisabled) return;

      let routeName;
      let routeParams;
      let routeQuery;

      if (this.params.type === "ASSIGNMENT_LESSON") {
        // TODO: SLS-11043 (DONE), attempt
        const moduleUuid = this.params.lesson.uuid;
        routeName = this.isStudent ? "AssignmentAttemptSectionCover" : "AssignmentViewSectionCover";
        routeParams = {
          assignmentUuid: this.params.assignmentUuid,
          moduleUuid,
          sectionId: sectionId,
        };
      } else {
        routeName = "EditorSectionCover";
        let resourceUuid = this.params.lesson.uuid;
        let resourceDomain = "";

        switch (this.params.lesson.resourceDomain) {
          case "PERSONAL":
            routeName = this.isStudent ? "PersonalLessonAttemptSectionCover" : routeName;
            resourceDomain = RESOURCE_DOMAIN_PATH.PERSONAL;
            break;
          case "MOE":
            routeName = this.isStudent ? "SelfStudyAttemptSectionCover" : routeName;
            resourceDomain = RESOURCE_DOMAIN_PATH.MOE_LIBRARY;
            resourceUuid = this.params.lesson.moeLibraryUuid;
            routeQuery = this.params.lesson.uuid ? { version: this.params.lesson.uuid } : undefined;
            break;
          case "COMMUNITY":
          case "Community":
            resourceDomain = RESOURCE_DOMAIN_PATH.COMMUNITY_GALLERY;
            resourceUuid = this.params.communityResourceUuid;
            break;
          case "ALP_TEMPLATE":
            resourceDomain = RESOURCE_DOMAIN_PATH.ALP_TEMPLATE;
            resourceUuid = this.params.templateUuid;
            break;
          case "ASSIGNMENT":
            routeName = this.isStudent ? "StudentGroupResourceAttemptSectionCover" : routeName;
            resourceDomain = RESOURCE_DOMAIN_PATH.CLASS_GROUP_RESOURCE;
            break;
        }

        routeParams = {
          resourceDomain: resourceDomain,
          lessonUuid: resourceUuid,
          mode: "view",
          sectionId: sectionId,
        };

        if (this.params.admin && this.params.admin === "admin") {
          routeParams.admin = "admin";
        }
      }

      const routeData = this.$router.resolve({
        name: routeName,
        params: routeParams,
        query: routeQuery,
      });
      window.open(routeData.href, "_blank");
    },

    handleSelect(sectionId, activityIds) {
      if (activityIds.length === 0) {
        this.$emit("update:selectionMode", "");
      } else {
        this.$emit("update:selectionMode", LESSON_ACTIVITY_TYPE.ACTIVITY);
      }
      this.selectedActivities = activityIds;

      if (!this.showSectionCover) {
        const sectionActivityList = this.getActivityList(sectionId);
        if (sectionActivityList.length === activityIds.length) {
          this.addToSelectAllActivities(sectionId);
        } else {
          this.removeFromSelectAllActivities(sectionId);
        }
      }
    },
    handleSelectOneActivity(activity) {
      this.selectedActivities = [];
      this.selectedActivities.push(activity.toString());
      this.selectActivity = activity.toString();
    },
    handleSelectOneSection(sectionId) {
      this.selectAllActivities = [];
      this.selectedActivities = [];
      const sectionActivityList = this.getActivityList(sectionId);
      // select all activities in section
      sectionActivityList.forEach((activity) => {
        this.selectedActivities.push(activity.id.toString());
      });
      this.selectActivity = sectionId.toString();
      this.addToSelectAllActivities(sectionId);

      // Collapse all expect the section that is selected
      const keys = Object.keys(this.expandedList);
      keys.forEach((i) => (this.expandedList[i].expanded = false));
      // Expand selected section
      this.expandedList[sectionId] = {
        expanded: true,
      };
    },
    handleSelectAll(sectionId, checked) {
      const sectionActivityList = this.getActivityList(sectionId);
      if (checked.includes(sectionId.toString())) {
        if (this.showSectionCover) this.$emit("update:selectionMode", LESSON_ACTIVITY_TYPE.SECTION);
        else this.$emit("update:selectionMode", LESSON_ACTIVITY_TYPE.ACTIVITY);

        // select all activities in section
        sectionActivityList.forEach((activity) => {
          if (!this.selectedActivities.includes(activity.id.toString())) {
            this.selectedActivities.push(activity.id.toString());
          }
        });
        this.addToSelectAllActivities(sectionId);
      } else {
        // unselect all activities in section
        sectionActivityList.forEach((activity) => {
          if (this.selectedActivities.includes(activity.id.toString())) {
            const index = this.selectedActivities.indexOf(activity.id.toString());
            this.selectedActivities.splice(index, 1);
          }
        });
        this.removeFromSelectAllActivities(sectionId);
        if (isEmpty(this.selectAllActivities)) {
          this.$emit("update:selectionMode", "");
        }
      }
    },

    addToSelectAllActivities(sectionId) {
      if (!this.selectAllActivities.includes(sectionId.toString())) {
        this.selectAllActivities.push(sectionId.toString());
      }
    },
    removeFromSelectAllActivities(sectionId) {
      if (this.selectAllActivities.includes(sectionId.toString())) {
        const index = this.selectAllActivities.indexOf(sectionId.toString());
        this.selectAllActivities.splice(index, 1);
      }
    },
    handleClearSelection() {
      this.$emit("update:selectionMode", "");
      this.selectAllActivities = [];
      this.selectedActivities = [];
    },
    isNotesEmpty(notes) {
      return isEmpty(notes);
    },
    handleNotesToggle(value) {
      this.dataShowNotes = value;
      this.$emit("toggle-notes", value);
    },
    initSubjectAndLevelDataTableOption() {
      this.subjectAndLevelDataTableHeaders = [
        {
          title: this.$i18n.t("search_resource_detail_subject_header_label"),
          class: "subject",
          key: "subjectName",
          sortable: false,
        },
        {
          title: this.$i18n.t("search_resource_detail_level_header_label"),
          class: "level",
          key: "levelAndStream",
          sortable: false,
        },
      ];

      this.subjectAndLevelDataTableFooterProps = {
        itemsPerPageOptions: [],
      };
    },
    checkSectionIdMatched(sectionId) {
      const section = this.params.highlights?.sections?.find((section) => section.id === sectionId);
      return !!section; // Returns true if a section is found, false otherwise
    },
    checkActivityIdMatched(activityId) {
      const activity = this.params.highlights?.activities?.find(
        (activity) => activity.id === activityId,
      );
      return !!activity; // Returns true if a activity is found, false otherwise
    },
    expandAllSections() {
      const keys = Object.keys(this.expandedList);
      keys.forEach((i) => (this.expandedList[i].expanded = true));
    },
    collapseAllSections() {
      const keys = Object.keys(this.expandedList);
      keys.forEach((i) => (this.expandedList[i].expanded = false));
    },
    fetchContributors() {
      return new Promise((resolve, reject) => {
        resourceApi
          .fetchContributors(this.params.lesson.uuid, this.params.lesson.resourceDomain)
          .then(
            //OK
            (response) => {
              if (response.code !== "ok") {
                reject(response.errorMap);
                return;
              }
              if (response.data.resourceContributor.length > 0) {
                this.creditedTo = response.data.resourceContributor.sort(
                  (a, b) => a.index - b.index,
                );
              } else {
                this.creditedTo = [];
              }
              resolve();
            },
            (error) => {
              reject(error);
            },
          );
      });
    },
    determineCreditedToTruncation() {
      if (!this.showCreditedTo) return;
      const creditedToContainer = DOMUtils.elOf(this.$el, ".creditedToContent")[0];
      if (creditedToContainer.clientHeight < creditedToContainer.scrollHeight) {
        this.isCreditedToExpanded = false;
      } else {
        this.truncateCreditedTo = false;
      }
    },
    handleToggleTruncation() {
      this.isCreditedToExpanded = !this.isCreditedToExpanded;
    },
    handleGameBookSelected(isSelected) {
      const gameBookId = this.params.lesson?.gameBookModel?.id;
      this.selectedGameBookId = isSelected && gameBookId ? gameBookId : null;
      this.$emit("gameBookSelected", this.selectedGameBookId);
    },
    emitScrollEvent(anchorId) {
      this.$emit("anchor", anchorId);
    },
    initSelectedGameBook() {
      this.selectedGameBookId = this.params.lesson?.gameBookModel?.id ?? null;
      if (this.selectedGameBookId) {
        this.handleGameBookSelected(this.selectedGameBookId);
      }
    },
  },
};
</script>

<style lang="scss">
@use "@styles/layout/z-map" as *;
@use "@styles/abstracts/mixins" as *;
@use "@styles/layout/breakpoint" as *;
@use "@styles/themes/sls-theme/tokens/colors" as *;
@use "@styles/themes/sls-theme/tokens/effects" as *;
@use "@styles/themes/sls-theme/tokens/spacing" as *;
@use "@styles/themes/sls-theme/tokens/typo" as *;

.search-course-resource-detail {
  &.show-notes {
    .additional-information {
      display: block;
    }
  }

  .course-details {
    .form-header {
      &.other-information {
        margin-bottom: $spacing-05;
      }
    }
    .field-value {
      // set a smaller value margin-top to minus off margin added in label-component
      margin-top: $spacing-02;

      .label-component {
        span {
          @include carbon--type-style("label-01", true);
          min-width: 2rem;
          padding: 0.375rem $spacing-03; // to increase pill height to 32px
          text-align: center;
          color: $text-01;
        }
      }
      .keywords {
        .bx--tag {
          padding: 0.375rem $spacing-03; // to increase pill height to 32px
        }
      }
    }
  }

  .additional-information {
    @include carbon--type-style("body-long-01");
    display: none;
    width: 100%;
    margin-top: $spacing-05;
    padding: $spacing-05;
    color: $text-01;
    background-color: $ui-background;
    border-radius: 10px;

    .empty-placeholder {
      margin: 0;

      .content {
        margin-bottom: 0;
      }

      .placeholder-icon {
        display: flex;
        align-items: center;
        justify-content: center;
        height: 80px;
        margin: 0;
      }

      svg {
        transform: scale(0.5);
      }
    }

    label {
      @include carbon--type-style("panel-title-01");
      display: flex;
      align-items: center;
      margin-bottom: $spacing-05;
      color: $text-02;

      .sls-icon {
        margin-right: $spacing-03;
      }
    }
  }

  .field-set {
    &.modified-by {
      .field-value {
        display: flex;
        align-items: center;
        flex-flow: row wrap;
        .date {
          margin-top: $spacing-02;
          flex: 0 0 100%;
        }
      }
      .creditor {
        width: auto;
        margin-right: $spacing-03;
      }
    }

    &.creditedTo {
      .creditedToContent {
        position: relative;
        word-break: break-word;

        &.collapsed {
          max-height: 4rem;
          overflow: hidden;

          > span {
            cursor: pointer;
            .ellipsis-text {
              cursor: pointer;
            }
          }

          &::before {
            content: "";
            position: absolute;
            height: 50%;
            width: 100%;
            bottom: 0;
            background-image: linear-gradient(rgba(255, 255, 255, 0) 50%, rgba($ui-01, 1));
            z-index: z-map("default");
          }
        }
      }

      .btn-toggle-truncation {
        @include carbon--type-style("card-short-01");
        margin-top: $spacing-01;
      }
    }
  }

  .notes-toggle {
    label {
      display: flex;
      flex-flow: row-reverse;
      align-items: center;
      gap: $spacing-02;
      .bx--toggle__switch {
        margin-top: 0;
      }
    }
  }

  .section-activity-list {
    .selection-bar {
      &._active {
        border-radius: 10px;
      }
    }

    .v-table__wrapper {
      .v-data-table-header {
        span {
          @include carbon--type-style("productive-heading-01");
        }
      }
      table {
        td {
          left: 0 !important;
          .table-cell-wrapper {
            > span {
              max-width: unset;
            }

            &.title {
              padding: $spacing-05;
              flex-flow: column wrap;
              align-items: flex-start;

              &.simple-view {
                .label-component,
                .notes {
                  display: none;
                }
              }

              &.has-input-box {
                .activity-checkbox {
                  margin-right: $spacing-03;
                }
                .activity-title-wrapper {
                  max-width: calc(100% - 7rem);
                }
              }

              > .activity-header-wrapper {
                width: 100%;
                display: flex;
                align-items: center;
              }

              .activity-title-wrapper {
                display: flex;
                flex-flow: row wrap;
                max-width: calc(100% - 4rem);

                .label-component {
                  max-width: calc(100% - 1rem);

                  > .ellipsis-text {
                    overflow: hidden;
                  }
                }
              }

              .activity-checkbox {
                & ~ .activity-title-wrapper {
                  max-width: calc(100% - 6.5rem);
                }
              }

              // Intended specificity to override generic styling
              &:not(.checkbox):not(.action) {
                margin-right: 0;

                @include media("tablet-max") {
                  .page-title {
                    align-self: center;
                    overflow: hidden;
                  }
                }
              }
            }
          }
        }
      }
      th {
        background-color: $ui-04;

        .all-checkbox {
          display: inline-flex;
          vertical-align: middle;
          margin-right: $spacing-03;
        }
      }
    }
    .bx--link {
      &:hover {
        color: $link-01;
        text-decoration: none;
      }
    }

    .view-ext {
      margin-left: auto;
      min-width: 24px;
      cursor: pointer;

      [stroke] {
        stroke: $interactive-01;
      }

      [fill] {
        fill: $interactive-01;
      }
    }

    .grouper {
      &.disabled {
        // For section-grouper's border, activity-grouper doesn't have
        border-top-color: $disabled-02;

        > div {
          pointer-events: none;
        }

        .bx--link {
          color: $text-03;
        }

        .icon {
          path {
            &[fill] {
              fill: $disabled-02;
            }
            &[stroke] {
              stroke: $disabled-02;
            }
          }
        }

        .page-title {
          color: $text-03;
        }

        .view-ext {
          [fill] {
            fill: $disabled-02;
          }
          [stroke] {
            stroke: $disabled-02;
          }
        }
      }
    }

    .activity-grouper {
      margin-top: $spacing-05;

      &.disabled {
        .page-label {
          color: $text-03;
        }
      }

      .v-table {
        .v-table__wrapper {
          border-radius: 10px;

          table {
            > thead {
              th {
                padding: $spacing-03 $spacing-05 $spacing-03 $spacing-05;
              }
            }
            > tbody {
              > tr {
                &:last-child {
                  td {
                    &:first-child {
                      border-bottom-left-radius: 10px;
                    }
                    &:last-child {
                      border-bottom-right-radius: 10px;
                    }
                  }
                }
                background-color: $ui-background;
                > td {
                  height: 100%;
                  background-color: $ui-background;
                  &.v-data-table__td {
                    &:nth-child(2) {
                      max-width: 300px;
                    }
                  }
                }
              }
            }
          }
        }
      }

      .additional-information {
        background-color: $ui-01;
      }
    }

    .section-grouper {
      position: relative;
      padding: $spacing-05;
      margin: $spacing-05 0;
      background-color: $ui-background;
      border: 1px solid $ui-04;
      border-top: 4px solid var(--color);
      border-radius: 10px;

      &.is-section-hidden {
        .icon {
          opacity: 0.5;
        }
      }
      .match-indicator {
        margin-bottom: -($spacing-05);
        padding: $spacing-05 0 $spacing-03 $spacing-05;
      }
    }

    .section-component {
      background-color: $ui-01;
      border-radius: 10px;

      .wrapper {
        display: grid;
        grid-template-columns: 2.5rem auto auto;
        align-items: center;
        padding: $spacing-05;

        &.has-input-box {
          grid-template-columns: 2rem 2.5rem auto auto;
        }

        .additional-information {
          grid-column: 1 / -1;
        }
      }

      .page-label {
        color: $ui-01;
      }
    }

    .section-info {
      display: flex;
      align-items: center;
      padding: $spacing-05 0 $spacing-03 0;

      .actions-buttons {
        @include carbon--type-style("card-short-01", true);
      }
    }

    .section-activities {
      margin-top: $spacing-03;
      background-color: $ui-01;
      border-radius: 10px;

      > .v-table {
        background-color: transparent;
        > .v-table__wrapper {
          border-radius: 10px;
        }

        tbody {
          > tr {
            > td {
              height: 100%;
              border: none;
              background-color: $ui-01;
              &.v-data-table__td {
                &:nth-child(2) {
                  max-width: 300px;
                }
              }
            }
          }
        }
      }
    }

    .page-title {
      @include carbon--type-style("panel-title-01", true);
      @include line-clamp(2);
      width: 100%;
      padding-right: $spacing-05;
      color: $text-02;
    }
    .icon {
      display: flex;
      flex: 0 0 auto;
      position: relative;
      margin-right: $spacing-03;
      .sls-icon {
        &[name="SectionHidden32"] {
          & ~ .page-label {
            display: none;
          }
        }
      }
    }

    .page-label {
      @include carbon--type-style("helper-text-01", true);
      position: absolute;
      top: 0.7rem;
      left: 50%;
      transform: translateX(-50%);
      color: $icon-01;
    }
  }

  .passcode-info {
    display: flex;
    margin: $spacing-05 0;
    &:first-child {
      margin-top: 0;
    }

    span {
      @include carbon--type-style("helper-text-01", true);
      margin-left: $spacing-03;
      color: $text-02;
    }
  }
}
</style>
